// ============================================================================
// HR Onboarding module — types + canonical checklist seed.
//
// Spec: cowork-prompt-hr-onboarding-module-2026-05-28 (Antonela §2 / §3.1 / §3.3).
//
// We deliberately keep these types module-local rather than adding them to
// src/shared/types.ts — the companion Hiring Pipeline cowork is editing
// shared/types.ts in parallel, and keeping our surface area out of that file
// avoids a guaranteed merge conflict. employee_onboarding + partner_onboarding
// column shapes are mirrored here from the live schema (verified 2026-05-28).
// ============================================================================

// ----- Onboarding stages (8, left-to-right) --------------------------------
// These are the canonical stage values. employee_onboarding rows authored by
// the Hiring handoff use these directly. partner_onboarding rows carry legacy
// free-text values (in_progress / Unknown / Incomplete) that we map to these
// at RENDER TIME only — see utils.mapStatusToStage. We never mutate the DB row
// for the mapping.
export const ONBOARDING_STAGES = [
  'Offer Accepted',
  'Documents Sent',
  'Documents Received',
  'Background Check',
  'Orientation Scheduled',
  'Orientation Complete',
  'First Shift Assigned',
  'Active',
] as const;

export type OnboardingStage = (typeof ONBOARDING_STAGES)[number];
export type OnboardingType = 'W2' | 'Partner' | 'Subcontractor';

// Terminal statuses live off the active board. 'Cancelled' is set by the
// Cancel Onboarding action; 'Complete' is a legacy partner terminal value.
export const TERMINAL_STATUSES = ['Cancelled', 'Complete', 'Completed'] as const;

export const STAGE_LABELS: Record<OnboardingStage, string> = {
  'Offer Accepted': 'OFFER ACCEPTED',
  'Documents Sent': 'DOCS SENT',
  'Documents Received': 'DOCS RECEIVED',
  'Background Check': 'BACKGROUND CHECK',
  'Orientation Scheduled': 'ORIENTATION SCHED',
  'Orientation Complete': 'ORIENTATION DONE',
  'First Shift Assigned': 'FIRST SHIFT',
  Active: 'ACTIVE',
};

// ----- Subcontractor-track words (item 871, Ben 2026-10-19) --------------------
// Display only. The stored status values above, the CHECK constraint and every
// function that compares them are unchanged; a sub or partner card shows these
// words for the same stored value. W2 cards keep STAGE_LABELS.
export const SUB_STAGE_LABELS: Record<OnboardingStage, string> = {
  'Offer Accepted': 'AGREEMENT SENT',
  'Documents Sent': 'DOCS SENT',
  'Documents Received': 'DOCS RECEIVED',
  'Background Check': 'BACKGROUND CHECK',
  'Orientation Scheduled': 'WALKTHROUGH SCHED',
  'Orientation Complete': 'WALKTHROUGH DONE',
  'First Shift Assigned': 'START DATE SET',
  Active: 'ACTIVE',
};

export const SUB_STAGE_NAMES: Record<OnboardingStage, string> = {
  'Offer Accepted': 'Agreement Sent',
  'Documents Sent': 'Documents Sent',
  'Documents Received': 'Documents Received',
  'Background Check': 'Background Check',
  'Orientation Scheduled': 'Site Walkthrough Scheduled',
  'Orientation Complete': 'Site Walkthrough Complete',
  'First Shift Assigned': 'Start Date Set',
  Active: 'Active',
};

/** A stored status as a person reads it on a card's detail: the sub-track name for a non-W2 card, else as stored. */
export function stageName(raw: string | null | undefined, type: OnboardingType): string | null {
  if (!raw) return null;
  if (type === 'W2') return raw;
  return (SUB_STAGE_NAMES as Record<string, string>)[raw] ?? raw;
}

/** The short stage label for a card of this type. */
export function stageLabel(stage: OnboardingStage, type: OnboardingType): string {
  return type === 'W2' ? STAGE_LABELS[stage] : SUB_STAGE_LABELS[stage];
}

// Checklist items whose words differ on the Partner + Subcontractor track. Keyed
// by the stored item key; the stored label on old rows is left as it is.
const SUB_CHECKLIST_LABELS: Record<string, string> = {
  offer_letter_signed: 'Agreement signed',
  direct_deposit: 'ACH authorization on file (link only — no bank info in DB)',
};

/** A checklist item's label as this card's type reads it. */
export function checklistLabel(key: string, label: string, type: OnboardingType): string {
  return (type !== 'W2' && SUB_CHECKLIST_LABELS[key]) || label;
}

/**
 * A partner document's type as a person reads it. The stored value stays
 * 'Direct_Deposit' (31 rows, the RLS mask and the sub-onboard function key on it);
 * the screen says what the form is for a business: an ACH payment authorization.
 */
export function partnerDocumentLabel(documentType: string | null | undefined): string {
  if (documentType === 'Direct_Deposit') return 'ACH Authorization';
  return documentType ?? '';
}

// ----- Type chip ------------------------------------------------------------
// The three values used both for the card chip AND for checklist applies_to
// filtering. partner_onboarding rows are 'Subcontractor'; employee_onboarding
// rows derive from employment_type. (OnboardingType is declared above, with the stages.)

export const TYPE_CHIP_LABELS: Record<OnboardingType, string> = {
  W2: 'W2 Employee',
  Partner: 'Partner',
  Subcontractor: 'Subcontractor',
};

// ----- Checklist ------------------------------------------------------------
export type ChecklistStatus = 'pending' | 'complete' | 'n_a' | 'blocked';

export interface ChecklistItem {
  key: string;
  label: string;
  status: ChecklistStatus;
  applies_to: OnboardingType[];
  notes?: string;
}

export type ChecklistSection = 'documents' | 'compliance' | 'training' | 'admin';

export const CHECKLIST_SECTIONS: ChecklistSection[] = [
  'documents',
  'compliance',
  'training',
  'admin',
];

export const SECTION_LABELS: Record<ChecklistSection, string> = {
  documents: 'Documents',
  compliance: 'Compliance',
  training: 'Training',
  admin: 'Admin',
};

// The canonical checklist shape: four section arrays. Extra (non-section)
// keys on a checklist_json blob (source / last_backfilled / coi_link / notes)
// are preserved on write — see utils.mergeChecklistIntoRaw.
export type CanonicalChecklist = Record<ChecklistSection, ChecklistItem[]>;

// ----- Unified onboarding record (UNION of both tables) ---------------------
export interface OnboardingRecord {
  onboarding_id: string;
  source: 'employee' | 'partner';
  type: OnboardingType;
  // The employment_type used for applies_to filtering (W2 / Partner / Subcontractor).
  employmentTypeForChecklist: OnboardingType;
  name: string;
  role: string | null;
  account: string | null;
  start_date: string | null;
  // Raw DB status (un-mapped) — kept so writes target the real value.
  rawStatus: string | null;
  // Display stage after render-only mapping.
  stage: OnboardingStage;
  // True when the record is terminal (Cancelled / Complete) and off the board.
  terminal: boolean;
  // Parsed canonical checklist (null when the blob isn't canonical-shaped).
  checklist: CanonicalChecklist | null;
  // The raw checklist_json from the DB, preserved for merge-on-write.
  rawChecklistJson: Record<string, unknown> | null;
  // TCSD-0162 — ownership is a POSITION with an optional named assignee.
  // Replaces hr_owner, which stored a person. Render with ownerLabel().
  owner_position_key: string | null;
  assigned_to_eid: string | null;
  assigned_am: string | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
  // employee_onboarding only
  hiring_request_id: string | null;
  position_id: string | null;
  employee_id: string | null;
  // partner_onboarding only
  provider_id: string | null;
  w9_link: string | null;
  // ⛔ No coi_expiration_date here. It was carried from
  // partner_onboarding.coi_expiration_date and read by nothing; the retired
  // column is being dropped. A sub's COI expiry lives on SubcontractorRow,
  // derived from partner_documents.
}

// ----- APPENDIX A — canonical 16-item checklist seed ------------------------
// Verbatim from the prompt. Used when a record has no canonical checklist.
export function canonicalSeed(): CanonicalChecklist {
  return {
    documents: [
      { key: 'offer_letter_signed', label: 'Offer letter signed', status: 'pending', applies_to: ['W2', 'Partner', 'Subcontractor'] },
      { key: 'i9', label: 'I-9 completed', status: 'pending', applies_to: ['W2'] },
      { key: 'w4', label: 'W-4 completed', status: 'pending', applies_to: ['W2'] },
      { key: 'w9', label: 'W-9 completed', status: 'pending', applies_to: ['Partner', 'Subcontractor'] },
      { key: 'direct_deposit', label: 'Direct deposit form on file (link only — no bank info in DB)', status: 'pending', applies_to: ['W2', 'Partner', 'Subcontractor'] },
      { key: 'handbook_signed', label: 'Employee handbook signed', status: 'pending', applies_to: ['W2'] },
    ],
    compliance: [
      { key: 'background_initiated', label: 'Background check initiated', status: 'pending', applies_to: ['W2', 'Partner', 'Subcontractor'] },
      { key: 'background_cleared', label: 'Background check cleared', status: 'pending', applies_to: ['W2', 'Partner', 'Subcontractor'] },
      { key: 'drug_test', label: 'Drug test (if required by account)', status: 'pending', applies_to: ['W2', 'Partner', 'Subcontractor'] },
      { key: 'coi_received', label: 'COI received (subcontractors)', status: 'pending', applies_to: ['Subcontractor'] },
      { key: 'uniform_issued', label: 'Uniform issued', status: 'pending', applies_to: ['W2', 'Partner'] },
    ],
    training: [
      { key: 'osha_basics', label: 'OSHA basics completed', status: 'pending', applies_to: ['W2', 'Partner'] },
      { key: 'infection_control', label: 'Infection control / terminal cleaning training', status: 'pending', applies_to: ['W2', 'Partner'] },
      { key: 'account_specific', label: 'Account-specific training (if applicable)', status: 'pending', applies_to: ['W2', 'Partner', 'Subcontractor'] },
      { key: 'first_shift_shadow', label: 'First shift shadow completed', status: 'pending', applies_to: ['W2', 'Partner'] },
    ],
    admin: [
      { key: 'added_to_scheduling', label: 'Added to scheduling system', status: 'pending', applies_to: ['W2', 'Partner', 'Subcontractor'] },
      { key: 'added_to_payroll', label: 'Added to payroll (via Finance → Gusto)', status: 'pending', applies_to: ['W2'] },
      { key: 'account_assignment', label: 'Account assignment confirmed', status: 'pending', applies_to: ['W2', 'Partner', 'Subcontractor'] },
      { key: 'emergency_contact', label: 'Emergency contact on file', status: 'pending', applies_to: ['W2'] },
    ],
  };
}
